import { MenuItem, Select, styled } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ChevronDown } from 'lucide-react';
import React, { useCallback, useState } from 'react';

// Container lays out label on the left and select on the right
const Root = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  width: '100%',
}));

const Label = styled('div')(({ theme }) => ({
  color: `${theme.palette.spruce['800Base']}`,
  fontSize: 14,
  lineHeight: '20px',
  userSelect: 'none',
}));

const StyledSelect = styled(Select, { shouldForwardProp: (prop) => prop !== 'align' })(
  ({ align, theme }) => ({
    height: '28px',
    backgroundColor: '#F9FAFA',
    borderRadius: 4,
    boxShadow: '0 -1px 0 0 rgba(0,0,0,0.20), 0 1px 0 0 #ffffff',
    '& .MuiSelect-select': {
      padding: '0 8px 0 12px',
      fontSize: 14,
      color: `${theme.palette.spruce['800Base']}`,
      textAlign: align || 'left',
      display: 'flex',
      alignItems: 'center',
      minHeight: 'auto',
      height: '26px',
    },
    '& .MuiSelect-select.MuiInputBase-inputSizeSmall': {
      paddingLeft: 2,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiSelect-icon': {
      color: `${theme.palette.spruce['800Base']}`,
      right: '8px',
      width: 16,
      height: 16,
    },
  }),
);

const ChevronIcon = (props) => <ChevronDown size={16} strokeWidth={1.5} {...props} />;

export const SelectStd = ({
  label = '',
  value = '',
  onChange,
  options = [],
  disabled = false,
  width = '80px',
  hideLabel = false,
  align = 'left',
  ...props
}) => {
  const theme = useTheme();
  const [hover, setHover] = useState(false);
  const handleEnter = useCallback(() => setHover(true), []);
  const handleLeave = useCallback(() => setHover(false), []);

  return (
    <Root
      data-hover={hover ? 'true' : 'false'}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      {...props}
    >
      {!hideLabel && <Label>{label}</Label>}
      <StyledSelect
        align={align}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        disabled={disabled}
        sx={{ width }}
        displayEmpty
        size="small"
        IconComponent={ChevronIcon}
        MenuProps={{
          PaperProps: {
            sx: {
              backgroundColor: '#F9FAFA',
              border: `1px solid ${theme.palette.iron['200Base']}`,
              boxShadow: '0 -1px 0 0 rgba(0,0,0,0.20), 0 1px 0 0 #ffffff',
              '& .MuiMenuItem-root:hover': {
                backgroundColor: theme.palette.shamrock[100],
              },
              '& .MuiMenuItem-root:focus': {
                backgroundColor: theme.palette.iron[100],
              },
            },
          },
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </StyledSelect>
    </Root>
  );
};

export default SelectStd;
